import React, { useCallback, useEffect, useState } from 'react';
import {
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Grid,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContentText,
} from '@mui/material';
import { UserRoundX, UserRoundPen } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { usersService } from '../../../services/users.service';
import { UserProfile } from '../../../types/auth.types';

const AccountSettingsContent: React.FC = () => {
  const { authStatus, refreshStatus } = useAuth();
  //const userData = (authStatus.metadata as Partial<JudgeProfile>) || {};
  // const memoizedUserData = useMemo(() => {
  //   const data = (authStatus.metadata as Partial<JudgeProfile>) || {};
  //   return {
  //     firstName: data.firstName || '',
  //     lastName: data.lastName || '',
  //     phone: data.phone || '',
  //   };
  // }, [authStatus.metadata]);

  const [deletionDialogOpen, setDeletionDialogOpen] = useState(false);

  // Editable fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');

  const loadUserData = useCallback((userData: Partial<UserProfile>) => {
    setFirstName(userData.firstName || '');
    setLastName(userData.lastName || '');
    setPhone(userData.phone || '');
  }, []);

  // Load data on mount
  useEffect(() => {
    if (authStatus.metadata) {
      loadUserData(authStatus.metadata);
    }
  }, [authStatus, loadUserData]);

  const handleUpdate = async (): Promise<void> => {
    if (!authStatus.user) return;

    try {
      await usersService.updateUserProfile(authStatus.user.uid, {
        firstName,
        lastName,
        phone,
      });

      // Refresh metadata so UI updates with latest info
      await refreshStatus();
    } catch (err) {
      console.error('Update failed:', err);
    }
  };

  const deleteAccount = async (): Promise<void> => {
    setDeletionDialogOpen(false);
    if (authStatus.user) {
      await usersService.deleteUserAccount(authStatus.user.uid);
      window.location.reload();
    }
  };

  const openConfirmDeletionDialog = () => {
    setDeletionDialogOpen(true);
  };

  return (
    <Box sx={{ maxWidth: 800 }}>
      <Typography variant="h4" gutterBottom color="primary" fontWeight="bold">
        Account Settings
      </Typography>
      <Paper sx={{ p: 3, mt: 2 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Email"
              value={authStatus.user?.email || ''}
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            {/* The phone number field is read-only for judges */}
            <TextField fullWidth label="Phone" value={phone} disabled />
          </Grid>

          <Grid item xs={12}>
            <Button
              variant="contained"
              color="primary"
              sx={{ mr: 2 }}
              startIcon={<UserRoundPen />}
              onClick={handleUpdate}
            >
              Update Account Info
            </Button>
            <Button
              variant="outlined"
              color="error"
              sx={{ mr: 2 }}
              startIcon={<UserRoundX />}
              onClick={openConfirmDeletionDialog}
            >
              Delete Account
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Dialog
        open={deletionDialogOpen}
        onClose={() => setDeletionDialogOpen(false)}
      >
        <DialogTitle>Delete Account and Data</DialogTitle>
        <DialogContentText sx={{ m: 2 }}>
          <Typography>
            Are you sure you want to delete your account? <br /> This cannot be
            undone and you will lose all judging progress.
          </Typography>
        </DialogContentText>
        <DialogActions>
          <Button onClick={() => setDeletionDialogOpen(false)}>Cancel</Button>
          <Button onClick={deleteAccount} variant="contained" color="error">
            Confirm Deletion
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccountSettingsContent;
